import React, { Children, cloneElement, createRef } from 'react';
import { isEqual } from 'lodash';
import ClassNames from 'classnames';
import {
  arrayOf,
  bool,
  func,
  node,
  oneOfType,
  shape,
  string
} from 'prop-types';
import BaseComponent from '../.internal/BaseComponent';
import SearchTextInput from './components/search_text_input/SearchTextInput';
import SelectOptionList from './components/select_option_list/SelectOptionList';
import SelectStatusIcon from './components/select_status_icon/SelectStatusIcon';
import SelectValue from './components/select_value/SelectValue';
import SelectOption from '../select_option/SelectOption';

import './Select.scss';

export default class Select extends BaseComponent {
  constructor(props) {
    super(props);

    this.state = {
      active: false,
      filteredOptions: [],
      isMouseHovered: true,
      optionData: {},
      options: [],
      searchValue: '',
      selected: null
    };

    this.selectRef = createRef();
  }

  componentDidMount() {
    const { value } = this.props;
    const { optionData } = this.state;

    if (typeof document !== 'undefined') {
      document.addEventListener('click', this.outsideClickHandler);
    }

    this.setState({
      selected: Object.prototype.hasOwnProperty.call(optionData, value)
        ? value
        : null
    });

    this.filterOptions();
  }

  componentDidUpdate(prevProps, prevState) {
    const { children, value } = this.props;
    const { searchValue } = this.state;

    if (
      !isEqual(children, prevProps.children) ||
      prevState.searchValue !== searchValue
    ) {
      this.filterOptions();
    }

    if (prevProps.value !== value) {
      // This was written before the team adopted its ESLint rules more
      // strictly. A proper fix may require a nontrivial refactor.
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ selected: null });
    }
  }

  componentWillUnmount() {
    if (typeof document !== 'undefined') {
      document.removeEventListener('click', this.outsideClickHandler);
    }
  }

  bindOptionData = (element, filterString, index, selected, disabled) => {
    const { props } = element;
    const optionObject = {
      option: {},
      optionData: {}
    };

    if (!props.children.toLowerCase().includes(filterString.toLowerCase())) {
      return null;
    }

    optionObject.option = cloneElement(element, {
      disabled,
      key: `option-${index}`,
      onClick: this.optionClickHandler,
      ref: this.optionsRef,
      selected
    });

    optionObject.optionData = {
      disabled,
      name: props.children,
      prefix: props.prefix,
      selected
    };

    return optionObject;
  };

  filterOptions = () => {
    const { searchValue } = this.state;
    const generatedOptions = this.generateOptions(searchValue);

    this.setState({
      ...generatedOptions,
      filteredOptions: generatedOptions.options
    });
  };

  generateOptions = (filterString = '') => {
    const { children } = this.props;
    const options = [];
    const optionData = {};

    Children.forEach(children, (element, index) => {
      const { props } = element;
      let updatedOption;

      if (typeof props.children === 'string') {
        updatedOption = this.bindOptionData(
          element,
          filterString,
          index,
          props.selected,
          props.disabled
        );

        if (updatedOption !== null) {
          options.push(updatedOption.option);
          optionData[props.value] = updatedOption.optionData;
        }
      } else {
        const groupOptions = [];

        Children.forEach(props.children, (groupElement, index2) => {
          const groupElementIndex = `${index}=${index2}`;
          const updatedGroupOption = this.bindOptionData(
            groupElement,
            filterString,
            groupElementIndex,
            groupElement.props.selected,
            groupElement.props.disabled || element.props.disabled
          );

          if (updatedGroupOption !== null) {
            groupOptions.push(updatedGroupOption.option);
            optionData[groupElement.props.value] =
              updatedGroupOption.optionData;
          }
        });

        if (groupOptions.length > 0) {
          options.push(
            cloneElement(element, {
              children: groupOptions,
              key: `option-group-${index}`,
              onClick: this.optionClickHandler,
              ref: this.optionsRef
            })
          );
        }
      }
    });

    return {
      options,
      optionData
    };
  };

  optionClickHandler = async optionValue => {
    const { onChange } = this.props;

    if (onChange) {
      await this.onChangeHandler(optionValue);
    }

    this.setState({
      active: false,
      searchValue: '',
      selected: optionValue
    });
  };

  onBlurHandler = () => {
    const { options, selected } = this.state;
    const { onBlur } = this.props;

    if (onBlur) {
      onBlur(selected, options);
    }
  };

  onChangeHandler = optionValue => {
    const { options } = this.state;
    const { onChange } = this.props;

    if (onChange) {
      onChange(optionValue, options);
    }
  };

  onClickHandler = () => {
    const { options, selected } = this.state;
    const { onClick, searchable } = this.props;

    this.setState({ active: true }, () => {
      if (searchable) {
        this.selectRef.current.querySelector('.search-text-input').focus();
      }
    });

    if (onClick) {
      onClick(selected, options);
    }
  };

  onFocusHandler = () => {
    const { options, selected } = this.state;
    const { onFocus } = this.props;

    if (onFocus) {
      onFocus(selected, options);
    }
  };

  onMouseEnter = () => {
    this.setState({ isMouseHovered: true });
  };

  onMouseLeave = () => {
    this.setState({ isMouseHovered: false });
  };

  outsideClickHandler = () => {
    const { isMouseHovered } = this.state;

    if (!isMouseHovered) {
      this.setState({
        active: false,
        searchValue: ''
      });
    }
  };

  setSearchResults = async searchValue => {
    this.setState({
      searchValue
    });
  };

  render() {
    const {
      className,
      disabled,
      id,
      invalid,
      loading,
      name,
      placeholder,
      readOnly,
      searchable,
      style,
      value
    } = this.props;
    const {
      active,
      filteredOptions,
      optionData,
      options,
      searchValue,
      selected
    } = this.state;

    const componentClassNames = ClassNames(
      'select',
      className,
      (disabled || readOnly) && 'select--disabled',
      invalid && 'select--invalid'
    );
    const searchInputClassNames = ClassNames(
      'selected-value',
      loading && 'loading'
    );

    const optionsDisplayed = (() => {
      let result = options;

      if (active && searchable) {
        result = filteredOptions;
      }

      if (result.length < 1 && searchValue.length > 0) {
        result = [
          <SelectOption
            className="select__empty-search-state"
            value={null}
            disabled
          >
            No results found
          </SelectOption>
        ];
      }

      return result;
    })();

    const { placeholderDisplayed, placeholderDisplayedPrefix } = (() => {
      const valueSource = value || selected;

      if (Object.prototype.hasOwnProperty.call(optionData, valueSource)) {
        return {
          placeholderDisplayed: optionData[valueSource].name,
          placeholderDisplayedPrefix: optionData[valueSource].prefix
        };
      }

      return {
        placeholderDisplayed: placeholder,
        placeholderDisplayedPrefix: null
      };
    })();

    const { valueUsed, valueUsedPrefix } = (() => {
      if (Object.prototype.hasOwnProperty.call(optionData, value)) {
        return {
          valueUsed: optionData[value].name,
          valueUsedPrefix: optionData[value].prefix
        };
      }

      if (Object.prototype.hasOwnProperty.call(optionData, selected)) {
        return {
          valueUsed: optionData[selected].name,
          valueUsedPrefix: optionData[selected].prefix
        };
      }

      return { valueUsed: null, valueUsedPrefix: null };
    })();

    const onBlurHandler =
      !disabled && !readOnly ? this.onBlurHandler : undefined;
    const onClickHandler =
      !disabled && !readOnly && !loading && !active
        ? this.onClickHandler
        : undefined;
    const onFocusHandler =
      !disabled && !readOnly ? this.onFocusHandler : undefined;

    return (
      <div
        className={componentClassNames}
        onMouseEnter={this.onMouseEnter}
        onMouseLeave={this.onMouseLeave}
        onBlur={this.outsideClickHandler}
      >
        <button
          type="button"
          className="select__input-wrapper"
          onFocus={onFocusHandler}
          onBlur={onBlurHandler}
          onClick={onClickHandler}
          ref={this.selectRef}
        >
          {active ? (
            <SearchTextInput
              className={searchInputClassNames}
              disabled={disabled || loading}
              id={id}
              invalid={invalid}
              name={name}
              onChange={this.setSearchResults}
              placeholder={placeholderDisplayed}
              prefix={placeholderDisplayedPrefix}
              readOnly={!searchable || readOnly}
              style={style}
              value={searchValue}
            />
          ) : (
            <SelectValue
              disabled={disabled || readOnly}
              id={id}
              invalid={invalid}
              label={valueUsed === null ? placeholder : valueUsed}
              name={name}
              prefix={valueUsedPrefix}
              value={valueUsed}
            />
          )}
          <div className="select__status-icon-container">
            <SelectStatusIcon
              active={active}
              disabled={disabled || loading || readOnly}
              invalid={invalid}
              loading={loading}
              ref={this.selectStatusIconRef}
            />
          </div>
        </button>
        <SelectOptionList active={active} options={optionsDisplayed} />
      </div>
    );
  }
}

Select.propTypes = {
  children: oneOfType([arrayOf(node), node]),
  className: string,
  disabled: bool,
  id: string,
  invalid: bool,
  loading: bool,
  name: string,
  onBlur: func,
  onChange: func,
  onFocus: func,
  placeholder: string,
  readOnly: bool,
  searchable: bool,
  style: shape({}),
  value: string
};

Select.defaultProps = {
  children: null,
  className: '',
  disabled: false,
  id: undefined,
  invalid: false,
  loading: false,
  name: '',
  onBlur: undefined,
  onChange: undefined,
  onFocus: undefined,
  placeholder: '',
  readOnly: false,
  searchable: true,
  style: undefined,
  value: undefined
};
